import { useEffect, useState, useRef } from 'react';

interface CalendlyWidgetProps {
  /** Custom class name for the widget container */
  className?: string;
  /** Custom inline styles for the widget container */
  style?: React.CSSProperties;
  /** Whether the widget should be active/visible (useful for dialogs) */
  isActive?: boolean;
}

// Declare Calendly on window object for TypeScript
declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (options: { url: string; parentElement: Element }) => void;
    };
  }
}

const CalendlyWidget: React.FC<CalendlyWidgetProps> = ({
  className = '',
  style,
  isActive = true,
}) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);

  // Load Calendly script and CSS
  useEffect(() => {
    // Check if Calendly script is already loaded
    if (window.Calendly) {
      setIsScriptLoaded(true);
      return;
    }

    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => {
      setIsScriptLoaded(true);
    };
    document.body.appendChild(script);

    // Also load the CSS
    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      // Cleanup only if elements exist
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  // Initialize Calendly widget when active and script is loaded
  useEffect(() => {
    if (isActive && isScriptLoaded && window.Calendly && widgetRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (widgetRef.current && window.Calendly) {
          window.Calendly.initInlineWidget({
            url: 'https://calendly.com/ironview360/visit',
            parentElement: widgetRef.current,
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isActive, isScriptLoaded]);

  if (!isScriptLoaded) {
    return (
      <div 
        className={`flex items-center justify-center bg-white rounded-lg ${className}`}
        style={{ minHeight: '500px', ...style }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-maintenance-navy mx-auto mb-4"></div>
          <p className="text-gray-600">Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={widgetRef}
      className={`calendly-inline-widget bg-white rounded-lg overflow-hidden ${className}`}
      style={{
        width: '100%',
        minHeight: '500px',
        ...style,
      }}
    />
  );
};

export default CalendlyWidget;
